import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import {
  GridCellParams,
  GridRenderCellParams,
  GridRowClassNameParams,
} from '@mui/x-data-grid';
import {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {WelcomeAlert} from '../../../redux/slices/alertSlice';
import AurButton from '../../common/aur-button/AurButton';

import {t} from 'i18next';
import {isDate} from 'lodash';
import {ReactComponent as RadioCheckedIcon} from '../../../assests/svg/radio-checked.svg';
import {ReactComponent as RadioUnCheckedIcon} from '../../../assests/svg/radio-unchecked.svg';
import {
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../../../constants/constants';
import {getUTCDateTimeForDisplay, isEmpty} from '../../../utils/utils';
import AurDataGridPro, {
  CustomGridColDef,
} from '../../common/aur-data-grid-pro/AurDataGridPro';
import AurPagination from '../../common/aur-pagination/AurPagination';
import AurTooltip from '../../common/aur-tooltip/AurTooltip';
import {useDatasetAlert} from './useDatasetAlert';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';

export interface DatasetAlert extends WelcomeAlert {
  datasets: string[];
}

interface DatasetAlertsDialogProps {
  onPressContinue?: (alert: DatasetAlert) => void;
}

export interface DatasetAlertsDialogRef {
  openDialog: (alerts: any) => void;
}

const DatasetAlertsDialog = forwardRef(
  (props: DatasetAlertsDialogProps, ref: Ref<DatasetAlertsDialogRef>) => {
    useImperativeHandle(ref, () => ({openDialog}));

    const {onPressContinue} = props;

    const {loading, datasetAlerts, getDatasetAlerts, reset} = useDatasetAlert();

    const [alertInfo, setAlertInfo] = useState<any>();
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [selectedAlert, setSelectedAlert] = useState<DatasetAlert>();

    const [totalItems, setTotalItems] = useState<number>(
      datasetAlerts?.length ?? 0
    );

    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(
      TABLE_PER_PAGE_DEFAULT_NO_ROW
    );

    useEffect(() => {
      if (isDialogOpen) {
        setSelectedAlert(undefined);
      }
    }, [isDialogOpen]);

    useEffect(() => {
      setTotalItems(datasetAlerts?.length ?? 0);
    }, [datasetAlerts]);

    const setItemsPerPageFromGrid = useCallback((pageSize: number) => {
      setCurrentPage(1);
      setItemsPerPage(pageSize);
    }, []);

    const openDialog = useCallback(
      (alerts: any) => {
        setIsDialogOpen(true);
        setAlertInfo(alerts);
        getDatasetAlerts(alerts.alertType, alerts.reportType, alerts.projectId);
      },
      [getDatasetAlerts]
    );

    const clearState = useCallback(() => {
      setIsDialogOpen(false);
      setAlertInfo(undefined);
      reset();
    }, [reset]);

    const onCancelDialog = useCallback(() => {
      setSelectedAlert(undefined);
      clearState();
    }, [clearState]);

    const onContinue = useCallback(() => {
      if (selectedAlert) {
        onPressContinue?.(selectedAlert);
      }

      clearState();
    }, [clearState, onPressContinue, selectedAlert]);

    const handleCellClick = useCallback(
      (params: GridCellParams) => {
        const {row} = params;
        if (
          selectedAlert
            ? row?.userAlertId === selectedAlert?.userAlertId
            : false
        ) {
          setSelectedAlert(undefined);
        } else {
          setSelectedAlert(row);
        }
      },
      [selectedAlert]
    );

    const renderAlertRadioButton = useCallback(
      (params: GridRenderCellParams) => {
        const {
          row: {userAlertId},
        } = params;

        const isSelected: boolean = selectedAlert
          ? selectedAlert?.userAlertId === userAlertId
          : false;

        return (
          <Box
            gap={'8px'}
            className={'aur-data-radio-button-cell'}
            tabIndex={0}
          >
            {isSelected ? <RadioCheckedIcon /> : <RadioUnCheckedIcon />}
            <AurTooltip title={params.value}>{params.value}</AurTooltip>
          </Box>
        );
      },
      [selectedAlert]
    );

    const TABLE_BASE_WIDTH = 1126;

    const columnArray: CustomGridColDef[] = useMemo(
      () => [
        {
          field: 'workspaceName',
          headerName: t(
            'welcomeScreenSingleClient.datasetAlertsModel.column.workspace'
          ),
          flex: 205 / TABLE_BASE_WIDTH,
          sortable: false,
          resizable: true,
          headerClassName: 'custom-header',
          renderCell: renderAlertRadioButton,
        },
        {
          field: 'triggeredOn',
          headerName: t(
            'welcomeScreenSingleClient.datasetAlertsModel.column.dateTriggered'
          ),
          flex: 145 / TABLE_BASE_WIDTH,
          sortable: false,
          resizable: true,
          type: 'date',
          headerClassName: 'custom-header',
          isDateColumn: true,
          valueGetter: (value) => {
            return new Date(value);
          },
          valueFormatter: (value) => {
            return isDate(value) ? getUTCDateTimeForDisplay(value) : '';
          },
        },
        {
          field: 'custodian',
          headerName: t(
            'welcomeScreenSingleClient.datasetAlertsModel.column.custodian'
          ),
          flex: 205 / TABLE_BASE_WIDTH,
          sortable: false,
          resizable: true,
          headerClassName: 'custom-header',
        },
        {
          field: 'dataset',
          headerName: t(
            'welcomeScreenSingleClient.datasetAlertsModel.column.dataset'
          ),
          flex: 132 / TABLE_BASE_WIDTH,
          sortable: false,
          resizable: true,
          headerClassName: 'custom-header',
          // valueFormatter: (value) => {
          //   return isArray(value) ? (value?.[0] ?? '') : '';
          // },
        },
      ],
      [renderAlertRadioButton]
    );

    const renderPopupTitle = useCallback(
      () => (
        <DialogTitle className="title-text-container">
          <Typography className="typo-text headline3_size heading-text title-text">
            {alertInfo?.alertTitle}
          </Typography>
          <Typography className="typo-text body2_size subheading subtitle-text">
            {t(
              'welcomeScreenSingleClient.datasetAlertsModel.datasetAlertsDescription'
            )}
          </Typography>
        </DialogTitle>
      ),
      [alertInfo?.alertTitle]
    );

    const getRowClassName = useCallback(
      (params: GridRowClassNameParams) => {
        const {
          row: {userAlertId},
        } = params;
        if (userAlertId === selectedAlert?.userAlertId) {
          return 'selected-dataset-alert-row';
        }
        return '';
      },
      [selectedAlert?.userAlertId]
    );

    const renderPopupContent = useCallback(
      () => (
        <Box className="dataset-alerts-grid-container">
          {loading ? (
            <Box className="loader-container">
              <AurLoadingIndicator />
            </Box>
          ) : (
            <DialogContent>
              <AurDataGridPro
                getRowId={(row) => row.userAlertId}
                className={'aur-data-grid'}
                getRowHeight={() => 56}
                columnHeaderHeight={64}
                rows={datasetAlerts}
                columns={columnArray}
                hideFooterPagination
                hideFooter
                disableColumnSorting
                hideFooterRowCount
                pagination
                paginationMode={'client'}
                initialState={{
                  pagination: {
                    paginationModel: {
                      page: currentPage - 1,
                      pageSize: itemsPerPage,
                    },
                  },
                }}
                pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
                paginationModel={{
                  page: currentPage - 1,
                  pageSize: itemsPerPage,
                }}
                disableVirtualization
                getRowClassName={getRowClassName}
                hideFooterSelectedRowCount
                onCellClick={handleCellClick}
                rowSelection={!isEmpty(selectedAlert)}
                checkBoxVisible={false}
                pointer={true}
                showPagination={false}
                defaultItemsPerPage={itemsPerPage}
                addedInDialog
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                classNameOfParentScollElement='dataset-alerts-grid-container'
              />
            </DialogContent>
          )}
        </Box>
      ),
      [
        loading,
        datasetAlerts,
        columnArray,
        currentPage,
        itemsPerPage,
        getRowClassName,
        handleCellClick,
        selectedAlert,
      ]
    );

    const renderPaginationControl = useCallback(() => {
      return (
        <Box className={'table-pagination-container'}>
          <AurPagination
            totalItems={totalItems}
            isWideUseVariant={false}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            perPageOptions={TABLE_PAGE_SIZE_OPTION}
            onPageChange={setCurrentPage}
            setItemsPerPage={setItemsPerPageFromGrid}
            ariaLabelNextPage={t('common.pagination.nextHint')}
            ariaLabelPreviousPage={t('common.pagination.previousHint')}
          />
        </Box>
      );
    }, [currentPage, itemsPerPage, setItemsPerPageFromGrid, totalItems]);

    const renderDialogActions = useCallback(() => {
      return (
        <DialogActions>
          <Grid container className={'dataset-alerts-action-buttons-container'}>
            <Grid item xs>
              {renderPaginationControl()}
            </Grid>
            <Grid item>
              <AurButton
                dataTestId={'cancelRelationshipDialogButton'}
                classNames={'typo-text buttonMedium_size cancel-button'}
                type={'exit'}
                size={'Small'}
                label={t('welcomeScreenSingleClient.datasetAlertsModel.cancel')}
                onClick={onCancelDialog}
              />
            </Grid>
            <Grid item>
              <AurButton
                dataTestId={'saveRelationshipDialogButton'}
                classNames={'typo-text buttonMedium_size continue-button'}
                type={'continue'}
                size={'Small'}
                label={t(
                  'welcomeScreenSingleClient.datasetAlertsModel.continue'
                )}
                disabled={isEmpty(selectedAlert)}
                onClick={onContinue}
              />
            </Grid>
          </Grid>
        </DialogActions>
      );
    }, [onCancelDialog, onContinue, renderPaginationControl, selectedAlert]);

    return (
      <Dialog
        data-testid={'dataset-alerts-dialog'}
        className={'popup aur-unsaved-changes-popup dataset-alerts-dialog'}
        open={isDialogOpen}
      >
        {renderPopupTitle()}
        {renderPopupContent()}
        {renderDialogActions()}
      </Dialog>
    );
  }
);

export default DatasetAlertsDialog;
